import { gpx as gpxToGeoJson } from '@tmcw/togeojson';
import { bboxOf, haversineM, totalLengthM, type Bbox, type Position } from './geo';

/**
 * GPX → route geometry (SPEC §1.2, §4.4). Both sides of the app parse with this
 * module: the API is the authority that stores the result, while the import
 * screen runs the identical code in the browser to preview and sanity-check a
 * file before anything is written.
 *
 * The caller supplies an already-parsed `Document` — the browser uses its native
 * `DOMParser`, the API uses `@xmldom/xmldom` — so this module stays free of any
 * environment-specific XML dependency.
 */

/** An element as far as this module reads it. */
export interface GpxElement {
  textContent: string | null;
  getElementsByTagName(tag: string): ArrayLike<GpxElement>;
}

/**
 * The sliver of the DOM this module needs. The browser's native `Document` and
 * `@xmldom/xmldom` both satisfy it structurally, so `packages/shared` compiles
 * with no DOM lib and stays usable from the API and the SPA alike.
 */
export interface GpxDocument {
  getElementsByTagName(tag: string): ArrayLike<GpxElement>;
}

/** Upper bound on stored positions per polyline; keeps `GET /routes` light. */
export const MAX_POINTS = 1500;

/**
 * Largest GPX document accepted. The import screen refuses anything bigger with
 * a readable message, and the API's body schema uses the same number so an
 * oversized file can never slip past the friendly check into a bare 400.
 * (The route's Fastify `bodyLimit` sits above this to leave room for JSON
 * escaping of the document.)
 */
export const MAX_GPX_BYTES = 15_000_000;

/**
 * Stroke width of a course on the coordinators' map, in pixels. Set per course
 * on Zemljevid → Trase, beside its colour.
 */
export const ROUTE_WEIGHT = { min: 1, max: 10, default: 3 } as const;

/**
 * Simplification tolerance in metres. At 8 m a 120 km alpine course keeps every
 * switchback that reads at map zoom while shedding GPS jitter.
 */
export const SIMPLIFY_TOLERANCE_M = 8;

export interface ParsedGpx {
  /** One or more polylines of `[lng, lat]`, simplified and ready to store. */
  lines: Position[][];
  /** Name suggested by the file itself, if it carries one. */
  name: string | null;
  /** `<wpt>` entries present in the file. Reported to the coordinator, not used. */
  waypointCount: number;
  /** Positions kept after simplification. */
  pointCount: number;
  /** Course length in metres, measured on the simplified geometry. */
  lengthM: number;
  bbox: Bbox | null;
}

/** GeoJSON geometry as stored on `route.geojson`. */
export interface RouteGeometry {
  type: 'LineString' | 'MultiLineString';
  coordinates: number[][] | number[][][];
}

/** Perpendicular distance from `p` to segment `a`–`b`, in metres. */
function perpendicularM(p: Position, a: Position, b: Position): number {
  // Local equirectangular projection — exact enough over a single segment.
  const kx = 111_320 * Math.cos((p[1] * Math.PI) / 180);
  const ky = 110_574;
  const px = p[0] * kx;
  const py = p[1] * ky;
  const ax = a[0] * kx;
  const ay = a[1] * ky;
  const dx = b[0] * kx - ax;
  const dy = b[1] * ky - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Ramer–Douglas–Peucker simplification. Unlike dropping every n-th position it
 * keeps the positions that carry the shape, so switchbacks survive at the same
 * point budget. Iterative to stay safe on a 30 000-point track.
 */
export function simplifyLine(line: Position[], toleranceM: number): Position[] {
  if (line.length <= 2) return line.slice();

  const keep = new Uint8Array(line.length);
  keep[0] = 1;
  keep[line.length - 1] = 1;

  const stack: [number, number][] = [[0, line.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxDist = -1;
    let index = -1;
    for (let i = first + 1; i < last; i += 1) {
      const d = perpendicularM(line[i]!, line[first]!, line[last]!);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (index !== -1 && maxDist > toleranceM) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }
  return line.filter((_, i) => keep[i] === 1);
}

/** Even decimation — the backstop when simplification alone stays over budget. */
function decimate(line: Position[], maxPoints: number): Position[] {
  if (line.length <= maxPoints) return line;
  const step = (line.length - 1) / (maxPoints - 1);
  const out: Position[] = [];
  for (let i = 0; i < maxPoints; i += 1) out.push(line[Math.round(i * step)]!);
  return out;
}

/**
 * Ceiling on what simplification is fed. RDP degrades to O(n²) when nearly
 * every position carries shape, and this runs on the browser's main thread
 * during import preview — an uncapped 20 000-point track stalls it for seconds.
 * Sampling first bounds the work: 6000 positions is one per 20 m on a 120 km
 * course, already finer than the 8 m tolerance can act on.
 */
const SIMPLIFY_INPUT_CAP = 6000;

/** Simplify to the shape tolerance, then hard-cap the position count. */
export function reduceLine(line: Position[], toleranceM = SIMPLIFY_TOLERANCE_M): Position[] {
  return decimate(simplifyLine(decimate(line, SIMPLIFY_INPUT_CAP), toleranceM), MAX_POINTS);
}

function isPlausible(p: Position): boolean {
  return (
    Number.isFinite(p[0]) &&
    Number.isFinite(p[1]) &&
    p[0] >= -180 &&
    p[0] <= 180 &&
    p[1] >= -90 &&
    p[1] <= 90
  );
}

/** Text of the first `<child>` inside the first `<parent>`, trimmed. */
function firstText(doc: GpxDocument, parent: string, child: string): string | null {
  const text = doc
    .getElementsByTagName(parent)[0]
    ?.getElementsByTagName(child)[0]
    ?.textContent?.trim();
  return text ? text : null;
}

/**
 * Read a GPX document into simplified route geometry. Elevation and timestamps
 * are dropped — the map draws a plan-view polyline.
 *
 * Throws when the document carries no track or route line, or when its
 * coordinates are out of range (a mis-exported or swapped lat/lng file).
 */
export function parseGpxDocument(doc: GpxDocument): ParsedGpx {
  // togeojson is typed against a full DOM `Document`. Widening is required
  // wherever a DOM lib is loaded (the SPA) and redundant where none is (the
  // API), so the rule is silenced rather than the cast dropped.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const collection = gpxToGeoJson(doc as unknown as Parameters<typeof gpxToGeoJson>[0]);

  const raw: Position[][] = [];
  for (const feature of collection.features) {
    const geom = feature.geometry;
    if (!geom) continue;
    if (geom.type === 'LineString') raw.push(geom.coordinates);
    else if (geom.type === 'MultiLineString')
      for (const line of geom.coordinates as Position[][]) raw.push(line);
  }

  // Keep [lng, lat] only, then drop anything too short to draw.
  const trimmed = raw
    .map((line) => line.map((p): Position => [p[0], p[1]]))
    .filter((line) => line.length >= 2);
  if (!trimmed.length) throw new Error('GPX ne vsebuje sledi (trk/rte).');

  for (const line of trimmed) {
    if (!line.every(isPlausible))
      throw new Error('GPX vsebuje neveljavne koordinate (izven obsega zemljepisne širine/dolžine).');
  }

  const lines = trimmed.map((line) => reduceLine(line));

  return {
    lines,
    name: firstText(doc, 'trk', 'name') ?? firstText(doc, 'metadata', 'name'),
    waypointCount: doc.getElementsByTagName('wpt').length,
    pointCount: lines.reduce((n, l) => n + l.length, 0),
    lengthM: Math.round(totalLengthM(lines)),
    bbox: bboxOf(lines),
  };
}

/** Parsed lines as the GeoJSON geometry stored on `route.geojson`. */
export function toRouteGeometry(lines: Position[][]): RouteGeometry {
  if (lines.length === 1) return { type: 'LineString', coordinates: lines[0]! };
  return { type: 'MultiLineString', coordinates: lines };
}

/** `route.geojson` back into the array-of-polylines form the helpers take. */
export function geometryToLines(geometry: RouteGeometry): Position[][] {
  return geometry.type === 'LineString'
    ? [geometry.coordinates as Position[]]
    : (geometry.coordinates as Position[][]);
}

/** Course length in metres straight from stored geometry. */
export function geometryLengthM(geometry: RouteGeometry): number {
  return Math.round(totalLengthM(geometryToLines(geometry)));
}

/** "42,6 km" — Slovenian decimal comma, one decimal place. */
export function formatKm(metres: number): string {
  return `${(metres / 1000).toFixed(1).replace('.', ',')} km`;
}

/** Distance between the first and last position of a route, in metres. */
export function endToEndM(lines: Position[][]): number {
  const first = lines[0]?.[0];
  const lastLine = lines[lines.length - 1];
  const last = lastLine?.[lastLine.length - 1];
  return first && last ? haversineM(first, last) : 0;
}
